import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CircularProgress,
  Switch,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useSnackbar } from "notistack";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ResizableDataGrid from "components/ResizableDataGrid";

function Offers() {
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form states for Add/Edit
  const [saleName, setSaleName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [status, setStatus] = useState("Inactive");

  const [editOfferModal, setEditOfferModal] = useState({
    open: false,
    offer_id: null,
    sale_name: "",
    start_date: "",
    end_date: "",
    discount_percentage: "",
    min_amount: "",
    status: "Inactive",
  });

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    offer_id: null,
    sale_name: "",
  });

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const hh = String(date.getUTCHours()).padStart(2, '0');
    const min = String(date.getUTCMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const columns = [
    {
      field: "offer_id",
      headerName: "ID",
      width: isSmall ? 70 : isMedium ? 90 : 100,
      minWidth: isSmall ? 60 : 80,
    },
    {
      field: "sale_name",
      headerName: "Sale Name",
      width: isSmall ? 150 : isMedium ? 200 : 250,
      minWidth: isSmall ? 120 : 180,
    },
    {
      field: "discount_percentage",
      headerName: "Discount %",
      width: isSmall ? 100 : isMedium ? 120 : 130,
      minWidth: isSmall ? 80 : 100,
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: "min_amount",
      headerName: "Min Amount",
      width: isSmall ? 110 : isMedium ? 130 : 140,
      minWidth: isSmall ? 90 : 110,
      valueFormatter: (params) => `₹${params.value}`,
    },
    {
      field: "status",
      headerName: "Status",
      width: isSmall ? 145 : isMedium ? 165 : 170,
      minWidth: isSmall ? 120 : 140,
      renderCell: (params) => (
        <MDBox display="flex" alignItems="center" gap={0.75}>
          <Switch
            checked={params.value === "Active"}
            onChange={() =>
              handleStatusToggle(params.row.offer_id, params.value === "Active" ? "Inactive" : "Active")
            }
          />
          <Chip
            size="small"
            label={params.value}
            color={params.value === "Active" ? "success" : "default"}
            variant={params.value === "Active" ? "filled" : "outlined"}
          />
        </MDBox>
      ),
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: isSmall ? 150 : isMedium ? 180 : 200,
      minWidth: isSmall ? 130 : 160,
      valueFormatter: (params) => new Date(params.value).toLocaleString('en-IN', { timeZone: 'UTC' }),
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: isSmall ? 150 : isMedium ? 180 : 200,
      minWidth: isSmall ? 130 : 160,
      valueFormatter: (params) => new Date(params.value).toLocaleString('en-IN', { timeZone: 'UTC' }),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: isSmall ? 180 : isMedium ? 200 : 220,
      minWidth: isSmall ? 140 : 175,
      renderCell: (params) => (
        <MDBox
          display="flex"
          gap={0.75}
          flexDirection={isSmall ? "column" : "row"}
          alignItems={isSmall ? "stretch" : "center"}
        >
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => handleEditClick(params.row)}
            sx={{
              width: isSmall ? "100%" : "auto",
              minWidth: isSmall ? 0 : 76,
              px: 1.5,
              py: 0.6,
              fontSize: "0.65rem",
            }}
          >
            Edit
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            sx={{
              width: isSmall ? "100%" : "auto",
              minWidth: isSmall ? 0 : 66,
              px: 1.5,
              py: 0.6,
              fontSize: "0.65rem",
            }}
          >
            Delete
          </MDButton>
        </MDBox>
      ),
    },
  ];

  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/offers/getall`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      if (res.data.success) {
        setOffers(res.data.data || []);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error fetching offers", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (offerId, newStatus) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/offers/update-status`,
        { offer_id: offerId, status: newStatus },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Status updated successfully", { variant: "success" });
        fetchOffers();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating status", { variant: "error" });
    }
  };

  const handleAddOffer = async () => {
    if (!saleName || !startDate || !endDate || discountPercentage === "") {
      enqueueSnackbar("Please fill all required fields", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/offers/add`,
        {
          sale_name: saleName,
          start_date: startDate,
          end_date: endDate,
          discount_percentage: Number(discountPercentage),
          min_amount: Number(minAmount || 0),
          status,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Offer added successfully", { variant: "success" });
        setSaleName("");
        setStartDate("");
        setEndDate("");
        setDiscountPercentage("");
        setMinAmount("");
        setStatus("Inactive");
        setShowAdd(false);
        fetchOffers();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error adding offer", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (offer) => {
    setEditOfferModal({
      open: true,
      offer_id: offer.offer_id,
      sale_name: offer.sale_name,
      start_date: formatDateForInput(offer.start_date),
      end_date: formatDateForInput(offer.end_date),
      discount_percentage: offer.discount_percentage,
      min_amount: offer.min_amount,
      status: offer.status,
    });
  };

  const handleUpdateOffer = async () => {
    const { offer_id, sale_name, start_date, end_date, discount_percentage, min_amount, status: editStatus } = editOfferModal;
    if (!sale_name || !start_date || !end_date || discount_percentage === "") {
      enqueueSnackbar("Please fill all required fields", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/offers/update`,
        {
          offer_id,
          sale_name,
          start_date,
          end_date,
          discount_percentage: Number(discount_percentage),
          min_amount: Number(min_amount || 0),
          status: editStatus,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Offer updated successfully", { variant: "success" });
        setEditOfferModal({ open: false });
        fetchOffers();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating offer", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (offer) => {
    setDeleteModal({
      open: true,
      offer_id: offer.offer_id,
      sale_name: offer.sale_name,
    });
  };

  const handleDeleteOffer = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/offers/delete`,
        { offer_id: deleteModal.offer_id },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Offer deleted successfully", { variant: "success" });
        setDeleteModal({ open: false });
        fetchOffers();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error deleting offer", { variant: "error" });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                color="white"
                bgColor="info"
                borderRadius="lg"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Offers & Sales Management
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => setShowAdd(!showAdd)}
                >
                  {showAdd ? "Hide Add Form" : "Add Offer"}
                </MDButton>
              </MDBox>
              
              {showAdd && (
                <MDBox p={3} m={2} sx={{ border: "1px solid #ddd", borderRadius: "8px" }}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Add New Offer
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        label="Sale Name"
                        fullWidth
                        value={saleName}
                        onChange={(e) => setSaleName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        label="Discount Percentage (%)"
                        type="number"
                        fullWidth
                        value={discountPercentage}
                        onChange={(e) => setDiscountPercentage(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        label="Minimum Amount (₹)"
                        type="number"
                        fullWidth
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        type="datetime-local"
                        label="Start Date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        type="datetime-local"
                        label="End Date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="medium" sx={{ height: "44.13px" }}>
                        <InputLabel id="add-status-label">Status</InputLabel>
                        <Select
                          labelId="add-status-label"
                          label="Status"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          sx={{ py: 1.2 }}
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <MDBox mt={3} display="flex" gap={2}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleAddOffer}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Offer"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => setShowAdd(false)}
                    >
                      Cancel
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}

              <MDBox p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" my={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <ResizableDataGrid
                    rows={offers.map((item, index) => ({ id: item.offer_id, ...item }))}
                    columns={columns}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit Offer Dialog */}
      <Dialog
        open={editOfferModal.open}
        onClose={() => setEditOfferModal({ open: false })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Edit Offer</MDTypography>
            <IconButton onClick={() => setEditOfferModal({ open: false })}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDInput
                  label="Sale Name"
                  fullWidth
                  value={editOfferModal.sale_name || ""}
                  onChange={(e) =>
                    setEditOfferModal({ ...editOfferModal, sale_name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  label="Discount Percentage (%)"
                  type="number"
                  fullWidth
                  value={editOfferModal.discount_percentage === undefined ? "" : editOfferModal.discount_percentage}
                  onChange={(e) =>
                    setEditOfferModal({ ...editOfferModal, discount_percentage: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  label="Minimum Amount (₹)"
                  type="number"
                  fullWidth
                  value={editOfferModal.min_amount === undefined ? "" : editOfferModal.min_amount}
                  onChange={(e) =>
                    setEditOfferModal({ ...editOfferModal, min_amount: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="medium">
                  <InputLabel id="edit-status-label">Status</InputLabel>
                  <Select
                    labelId="edit-status-label"
                    label="Status"
                    value={editOfferModal.status || "Inactive"}
                    onChange={(e) =>
                      setEditOfferModal({ ...editOfferModal, status: e.target.value })
                    }
                    sx={{ py: 1.2 }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="datetime-local"
                  label="Start Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editOfferModal.start_date || ""}
                  onChange={(e) =>
                    setEditOfferModal({ ...editOfferModal, start_date: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="datetime-local"
                  label="End Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editOfferModal.end_date || ""}
                  onChange={(e) =>
                    setEditOfferModal({ ...editOfferModal, end_date: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDBox px={3} pb={2} display="flex" gap={2}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleUpdateOffer}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </MDButton>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => setEditOfferModal({ open: false })}
            >
              Cancel
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Offer</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to delete the offer <strong>{deleteModal.sale_name}</strong>?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDBox px={3} pb={2} display="flex" gap={2}>
            <MDButton variant="gradient" color="error" onClick={handleDeleteOffer}>
              Delete
            </MDButton>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => setDeleteModal({ open: false })}
            >
              Cancel
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Offers;
